namespace appn.views.reports.viewReport {
    interface ReportParameterDef {
        Name: string;
        Kind: string | number; // supports either enum name or numeric value
        HintText?: string;
        PlaceholderText?: string;
        IsOptional?: boolean;
    }

    type ReportDefs = { [reportTypeName: string]: ReportParameterDef[] };

    export function init(): void {
        addEvents();
        
        // initial render if a selection already exists
        renderForSelected();
    }

    function addEvents(): void {
        // No dropdown anymore; parameters are provided on initial load.
    }

    function renderForSelected(): void {
        const paramsForSelection: ReportParameterDef[] = getInitialParams();
        const $container = $('#report-parameters');
        $container.empty();

        if (!paramsForSelection || paramsForSelection.length === 0)
            return;

        // Render supported kinds: Text, DateTime
        paramsForSelection.forEach((p, idx) => {
            const inputId = `rp_${idx}`;

            // Label (shared styling for all kinds)
            const label = $('<label/>')
                .attr('for', inputId)
                .addClass('col-form-label')
                .text(p.Name || '');

            // Add help popup like Appn().LabelFor uses (Bootstrap Popover) with server-like gating/encoding
            if (p.HintText && p.HintText.trim().length > 0) {
                const normalizeNewlines = (s: string) => s.replace(/\r/g, '').replace(/\n/g, '<br>');
                const htmlEncode = (s: string) => $('<div/>').text(s).html();

                let hint = normalizeNewlines(p.HintText.trim());
                let validHint = '';
                if (hint.indexOf('help.appenate') >= 0) {
                    if ((window as any).appn && (window as any).appn.isDefaultRequest)
                        validHint = hint;
                } else {
                    validHint = hint;
                }

                if (validHint) {
                    const $hintWrapper = $('<span/>')
                        .attr('data-widget', 'popover')
                        .attr('data-trigger', 'manual')
                        .attr('data-html', 'true')
                        .attr('data-placement', 'top')
                        .attr('data-content', htmlEncode(validHint));
                    const $inner = $('<span/>');
                    $inner.text(p.Name || '');
                    const $icon = $('<i/>').addClass('fa fa-question-circle');
                    $hintWrapper.append($inner).append('&nbsp;&nbsp;').append($icon);
                    label.empty().append($hintWrapper);
                }
            }

            const $row = $('<div/>').addClass('form-group row');
            const $labelCol = $('<div/>').addClass('col-md-3').append(label);
            const $inputCol = $('<div/>').addClass('col-md-6');

            // Always post the Kind so MVC binds enum correctly
            const kindName = toEnumName(p.Kind);
            const $hiddenKind = $('<input/>')
                .attr('type', 'hidden')
                .attr('name', `ReportParameters[${idx}].Kind`)
                .val(kindName);
            $inputCol.append($hiddenKind);

            if (isTextKind(p.Kind)) {
                const $input = $('<input/>')
                    .attr('type', 'text')
                    .addClass('form-control')
                    .attr('id', inputId)
                    .attr('name', `ReportParameters[${idx}].Value`)
                    .attr('placeholder', p.PlaceholderText || '');

                applyRequiredIfNeeded($input, p);

                $inputCol.append($input);
            }
            else if (isIntegerKind(p.Kind)) {
                // Integer-only numeric input, styled like other controls
                const $input = $('<input/>')
                    .attr('type', 'number')
                    .attr('step', '1')
                    .attr('inputmode', 'numeric')
                    .addClass('form-control')
                    .attr('id', inputId)
                    .attr('name', `ReportParameters[${idx}].Value`)
                    .attr('placeholder', p.PlaceholderText || '');

                applyRequiredIfNeeded($input, p);

                // Prevent non-integer characters like e, E, +, ., ,
                $input.on('keydown', function (e) {
                    const code = e.key || '';
                    const disallow = ['e','E','+','.','-',','];
                    // allow '-' only at start and only once
                    if (code === '-') {
                        const v = (this as HTMLInputElement).value || '';
                        const isSelectionCoversAll = (this as HTMLInputElement).selectionStart === 0 && (this as HTMLInputElement).selectionEnd === v.length;
                        if (v.indexOf('-') >= 0 && !isSelectionCoversAll) { e.preventDefault(); }
                        if ((this as HTMLInputElement).selectionStart !== 0) { e.preventDefault(); }
                        return; // handled
                    }
                    if (disallow.indexOf(code) >= 0) {
                        e.preventDefault();
                    }
                });
                // Sanitize on input to keep only optional leading '-' and digits
                $input.on('input', function () {
                    let v = (this as HTMLInputElement).value || '';
                    if (!/^\-?\d*$/.test(v)) {
                        const negative = v.startsWith('-');
                        v = v.replace(/[^\d]/g, '');
                        (this as HTMLInputElement).value = (negative ? '-' : '') + v;
                    }
                });

                $inputCol.append($input);
            }
            else if (isDecimalKind(p.Kind)) {
                // Decimal input using text + pattern to avoid type=number locale quirks and caret jumps
                const $input = $('<input/>')
                    .attr('type', 'text')
                    .attr('inputmode', 'decimal')
                    .attr('pattern', '^-?\\d*(?:\\.\\d*)?$')
                    .addClass('form-control')
                    .attr('id', inputId)
                    .attr('name', `ReportParameters[${idx}].Value`)
                    .attr('placeholder', p.PlaceholderText || '');

                applyRequiredIfNeeded($input, p);

                // Keep last valid state to restore on invalid edits without breaking caret position
                const DECIMAL_RE = /^-?\d*(?:\.\d*)?$/;
                const rememberState = (el: HTMLInputElement) => {
                    const $el = $(el);
                    $el.data('prevVal', el.value || '');
                    try {
                        $el.data('selStart', el.selectionStart ?? (el.value || '').length);
                        $el.data('selEnd', el.selectionEnd ?? (el.value || '').length);
                    } catch (err) { /* ignore if selection APIs not available */ }
                };

                $input.each(function () { rememberState(this as HTMLInputElement); });

                // Key filtering: allow digits, one leading '-', single '.', block e/E/+/, and allow control keys
                $input.on('keydown', function (e) {
                    const key = e.key || '';
                    const el = this as HTMLInputElement;
                    const v = el.value || '';
                    const selStart = el.selectionStart ?? v.length;
                    const selEnd = el.selectionEnd ?? v.length;

                    // Control/navigation keys
                    const allowedControl = [
                        'Backspace','Delete','ArrowLeft','ArrowRight','ArrowUp','ArrowDown','Home','End','Tab','Enter'
                    ].includes(key);
                    if (allowedControl)
                        return;

                    // Disallow scientific notation and plus/comma
                    if (key === 'e' || key === 'E' || key === '+' || key === ',') {
                        e.preventDefault();
                        return;
                    }

                    if (key === '-') {
                        const selectionCoversAll = selStart === 0 && selEnd === v.length;
                        const alreadyHasMinus = v.indexOf('-') >= 0;
                        const atStart = selStart === 0;
                        if ((alreadyHasMinus && !selectionCoversAll) || !atStart) {
                            e.preventDefault();
                        }
                        return;
                    }

                    if (key === '.') {
                        const selectionCoversAll = selStart === 0 && selEnd === v.length;
                        const alreadyHasDot = v.indexOf('.') >= 0;
                        if (alreadyHasDot && !selectionCoversAll) {
                            e.preventDefault();
                        }
                        return;
                    }

                    // Otherwise only digits
                    if (!/^\d$/.test(key)) {
                        e.preventDefault();
                        return;
                    }
                });

                // On input, validate against regex. If invalid, restore previous value and caret.
                $input.on('input', function () {
                    const el = this as HTMLInputElement;
                    const v = el.value || '';
                    if (!DECIMAL_RE.test(v)) {
                        const $el = $(el);
                        const prevVal = ($el.data('prevVal') as string) ?? '';
                        const selStart = ($el.data('selStart') as number) ?? prevVal.length;
                        const selEnd = ($el.data('selEnd') as number) ?? prevVal.length;
                        el.value = prevVal;
                        try { el.setSelectionRange(selStart, selEnd); } catch (err) { /* ignore */ }
                    } else {
                        // valid -> remember current state
                        try {
                            const start = el.selectionStart ?? v.length;
                            const end = el.selectionEnd ?? start;
                            $(el).data('prevVal', v).data('selStart', start).data('selEnd', end);
                        } catch (err) {
                            $(el).data('prevVal', v);
                        }
                    }
                });

                $inputCol.append($input);
            }
            else if (isBooleanKind(p.Kind)) {
                // Render checkbox in the same structure/style as Appn().CheckBoxFor: wrapper + checkbox + inline label
                // Avoid duplicate left-side label; CheckBoxFor places the text next to the checkbox
                const labelHtml = (label && (label as any).html) ? (label as any).html() : (p.Name || '');
                $labelCol.empty();

                const $hiddenFalse = $('<input/>')
                    .attr('type', 'hidden')
                    .attr('name', `ReportParameters[${idx}].Value`)
                    .val('false');

                const $checkbox = $('<input/>')
                    .attr('type', 'checkbox')
                    .attr('id', inputId)
                    .attr('name', `ReportParameters[${idx}].Value`)
                    .val('true');

                // Wrapper with icheck + appn-checkbox classes (default Primary color)
                const $wrapper = $('<div/>')
                    .addClass('icheck-primary')
                    .addClass('appn-checkbox');

                // Inline label associated with the checkbox (reuse hint-enhanced HTML)
                const $inlineLabel = $('<label/>')
                    .attr('for', inputId)
                    .html(labelHtml || '');

                // Do NOT apply required here; matching CheckBoxFor behavior
                $wrapper.append($checkbox).append($inlineLabel);
                $inputCol.append($hiddenFalse).append($wrapper);
            }
            else if (isDateTimeKind(p.Kind)) {
                // Build the same structure as Appn().DateTimePicker / DatePickerFor
                const $group = $('<div/>').addClass('input-group date datetimepicker');
                const $input = $('<input/>')
                    .attr('type', 'text')
                    .addClass('form-control')
                    .attr('id', inputId)
                    .attr('name', `ReportParameters[${idx}].Value`)
                    .attr('placeholder', p.PlaceholderText || '');
                applyRequiredIfNeeded($input, p);

                const $icon = $('<i/>').addClass('fa fa-calendar fa-xs');
                const $btn = $('<button/>').attr('type', 'button').addClass('input-group-text btn btn-secondary').append($icon);
                const $append = $('<div/>').addClass('input-group-append input-group-addon').append($btn);

                $group.append($input).append($append);
                $inputCol.append($group);

                // Initialize the dynamic DateTime picker to ensure proper behavior/format
                try { (appn.ui && (appn.ui as any).dateTimePicker) ? (appn.ui as any).dateTimePicker($group) : null; } catch (e) { }
            } else {
                // Unsupported kinds are skipped for now
            }

            $row.append($labelCol).append($inputCol);
            $container.append($row);
        });
        try { (appn.popover && appn.popover.init) ? appn.popover.init() : null; } catch (e) { }
    }

    function applyRequiredIfNeeded($el: JQuery<HTMLElement>, p: ReportParameterDef): void {
        if (!p || !p.IsOptional) {
            $el.attr('required', 'required').attr('aria-required', 'true');
        }
    }

    function getInitialParams(): ReportParameterDef[] {
        const $hidden = $('#report-params');
        if ($hidden.length === 0)
            return [];
        const val = ($hidden.val() as string) || '';
        if (!val || !val.trim())
            return [];
        try {
            return JSON.parse(val) as ReportParameterDef[];
        } catch (e) {
            return [];
        }
    }

    function isTextKind(kind: string | number): boolean {
        if (typeof kind === 'number')
            return kind === 0; // ReportParameterKind.Text = 0
        if (!kind)
            return false;
        return (kind as string).toLowerCase() === 'text';
    }

    function isDateTimeKind(kind: string | number): boolean {
        if (typeof kind === 'number')
            return kind === 6; // ReportParameterKind.DateTime = 6 (see ReportUtils.ReportParameterKind)
        if (!kind)
            return false;
        return (kind as string).toLowerCase() === 'datetime' || (kind as string).toLowerCase() === 'dateTime'.toLowerCase();
    }

    function isIntegerKind(kind: string | number): boolean {
        if (typeof kind === 'number')
            return kind === 1; // ReportParameterKind.Integer = 1
        if (!kind)
            return false;
        return (kind as string).toLowerCase() === 'integer' || (kind as string).toLowerCase() === 'int';
    }

    function isDecimalKind(kind: string | number): boolean {
        if (typeof kind === 'number')
            return kind === 2; // ReportParameterKind.Decimal = 2
        if (!kind)
            return false;
        const k = (kind as string).toLowerCase();
        return k === 'decimal' || k === 'number' || k === 'float' || k === 'double';
    }

    function isBooleanKind(kind: string | number): boolean {
        if (typeof kind === 'number')
            return kind === 3; // ReportParameterKind.Boolean = 3
        if (!kind)
            return false;
        const k = (kind as string).toLowerCase();
        return k === 'boolean' || k === 'bool';
    }

    function toEnumName(kind: string | number): string {
        if (typeof kind === 'string') {
            // Normalize capitalization to match C# enum names
            const k = kind.trim();
            const l = k.toLowerCase();
            // Common aliases
            if (l === 'datetime') return 'DateTime';
            if (l === 'multiselect' || l === 'multi-select') return 'MultiSelect';
            if (l === 'number' || l === 'float' || l === 'double') return 'Decimal';
            if (l === 'bool') return 'Boolean';
            // Capitalize first letter; handle typical cases
            return k.charAt(0).toUpperCase() + k.slice(1);
        }
        // Numeric mapping using current enum order in ReportUtils.ReportParameterKind
        const map = ['Text','Integer','Decimal','Boolean','Date','Time','DateTime','Enum','Select','MultiSelect','Guid'];
        return map[kind] ?? 'Text';
    }

    // Ensure this page logic initializes when the app is ready, without any Razor inline script
    try {
        appn.pubSub.subscribe(appn.pubSub.APPN_READY, () => init());
    } catch (e) { /* swallow if pubSub not ready for some reason */ }

}
